import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Accordion from "src/components/Accordion";
import Attachments from "src/components/Attachments";
import ListImportFiles from "src/components/ListImportFiles";
import LovDelimiter from "src/components/Lov/Delimiter";
import Modal from "src/components/Modal";
import { ModalCenter } from "src/components/Modal/styles";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { IModalImportStructure, IRefModalImportStructure } from "./interface";

const ModalImportStructure: React.ForwardRefRenderFunction<IRefModalImportStructure, IModalImportStructure> = ({
	onClose
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal export delimited structure
	const [importedFiles, setImportedFiles] = useState([]);
	const modalImportStructure = useRef<RCCRefModalProps>(null);
	const refScrollTo = useRef<HTMLDivElement>(null);
	const { handleSubmit, control, setValue, reset, watch } = useForm();
	const openModalImportStructure  = () => {
		reset();
		setImportedFiles([]);
		modalImportStructure.current?.openModal();
	};
	const submitImportScructure = async (data: any) => {
		try{
			if(data.dropZone.length === 0){
				addToast({
					color: "yellow",
					description: translate("É necessário ao menos um arquivo para importar"),
					title: translate("Atenção")
				});
				return;
			}
			modalImportStructure.current?.setLoadingButton();
			const formData = new FormData();

			formData.append("Delimiter", data.delimiter);

			data.dropZone.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			const response = await RCCServicesApi.postRequest({
				method: "FinancialManagementAccounting/ImportValues",
				contentType: "multipart/form-data",
				body: formData
			});

			if(response.data.length > 0)
				setImportedFiles(() => response.data);

			refScrollTo.current?.scrollIntoView({
				behavior: "smooth"
			});

			addToast({
				color: "blue",
				description: translate("Arquivos processados. Verifique o status da integração."),
				title: translate("Atenção")
			});
            
			modalImportStructure.current?.resetLoadingButton();
		}catch(err){
			modalImportStructure.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao integrar valores"), addToast);
		}
	};

	const delimiter = watch("delimiter") ?? ";";

	useImperativeHandle(ref, () => ({
		openModalImportStructure
	}));

	return (
		<Modal 
			ref={modalImportStructure}
			header={{
				title: translate("Importar valores contábeis"),
				icon: {
					name: "upload"
				}
			}}
			styleInline={`${ModalCenter} height: 80vh;`}
			onSubmit={handleSubmit(submitImportScructure)}
			onClose={onClose}
		>		
			<Title color="purple" size="md">
				{translate("Instruções")}
			</Title>
			<div style={{ marginLeft: "1rem"}}>
				<Title color="grey">
					{translate("Você pode utilizar arquivos nas extensões (.txt, .csv, .xls, .xlsx ou .ods)")}
				</Title>
				<Title color="grey">
					{translate("Cada arquivo poderá conter no máximo 20Mb de tamanho")}
				</Title>
			</div>
			<Accordion 
				header={<>
					Layout
				</>}
			>
				<Title color="grey">
					{translate("Cada linha do arquivo é composta das seguintes informações (separados pelo delimitador)")} {delimiter}
				</Title>
				<hr />
				<RCCGrid container item >
					<Title color="defaultColor" size="xs">
						{translate("Unidade/Filial")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Data")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Cód. Centro de custo")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Descrição")} {translate("Centro de custo")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Cód. Conta contábil")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Descrição")} {translate("Conta contábil")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Cód. Item 1")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Descrição")} Item 1{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Cód. Item 2")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Descrição")} Item 2{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Crédito")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Débito")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Saldo")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Centro de custo")} {translate("Superior")}{delimiter}
					</Title>
					<Title color="defaultColor" size="xs">
						{translate("Conta")} {translate("Superior")}{delimiter}
					</Title>
				</RCCGrid>
				<hr />
			</Accordion>
				
			<br />
			<RCCGrid container item>
				<RCCGrid container item xs={12} sm={4} md={3} lg={2}>
					<LovDelimiter
						control={control}
						name="delimiter"
					/>
				</RCCGrid>
			</RCCGrid>
			<Attachments 
				name="dropZone"
				setValue={setValue}
				acceptedFormats=".csv,.txt,.xls,.xlsx,.ods"
			/>

			<br />
			<ListImportFiles importedFiles={importedFiles} />
			<div ref={refScrollTo}/>
		</Modal>
	);
};

export default forwardRef(ModalImportStructure);
